// Custom Parchment format definitions for DocumentEditor.
// Import from here instead of defining inline in DocumentEditor.tsx.
//
// DocumentTableBlot is NOT registered with Quill — quill-table-better owns the
// table blot; a competing registration would conflict.
//
// applyDocumentTableClassToHtml operates on an off-screen div and never touches
// quill.root, so it avoids the MutationObserver re-normalization loop that would
// break quill-table-better's table insertion state machine.

import Quill from "quill";
import type { ClassAttributor as ClassAttributorType, Scope as ScopeType } from "parchment";

// Pull Parchment off Quill.imports instead of importing the "parchment" package
// directly: a stray hoisted parchment@1.1.4 (from @types/quill's dependencies)
// can shadow quill's own bundled parchment@3.0.0 in node_modules, which lacks
// named exports like ClassAttributor and breaks `new ClassAttributor(...)`.
const Parchment = Quill.import("parchment") as {
  ClassAttributor: typeof ClassAttributorType;
  Scope: typeof ScopeType;
};
const { ClassAttributor, Scope } = Parchment;

export class DocumentTableBlot {
  static readonly blotName = "ql_table";
  static readonly tagName = "table";
  static readonly className = "ql_table";
}

export function applyDocumentTableClassToHtml(html: string): string {
  const div = document.createElement("div");

  div.innerHTML = html;
  div
    .querySelectorAll<HTMLTableElement>(
      `table:not(.${DocumentTableBlot.className})`,
    )
    .forEach((table) => table.classList.add(DocumentTableBlot.className));

  return div.innerHTML;
}

let reveFormatsRegistered = false;

/**
 * Extends Quill's built-in blots so that every toolbar format action also stamps
 * the matching ql_ CSS class (from LetterStylesheet.css) onto the content node.
 * Must be called once before the first Quill instance is created.
 */
export function registerReveFormats(): void {
  if (reveFormatsRegistered) return;
  reveFormatsRegistered = true;

  // ── Inline blots ──────────────────────────────────────────────────────────

  const Bold = Quill.import("formats/bold") as any;
  class ReveBold extends Bold {
    static create(value: any) {
      const node = super.create(value) as HTMLElement;
      node.classList.add("ql_bold");
      return node;
    }
  }
  Quill.register(ReveBold, true);

  const Italic = Quill.import("formats/italic") as any;
  class ReveItalic extends Italic {
    static create(value: any) {
      const node = super.create(value) as HTMLElement;
      node.classList.add("ql_italic");
      return node;
    }
  }
  Quill.register(ReveItalic, true);

  const Underline = Quill.import("formats/underline") as any;
  class ReveUnderline extends Underline {
    static create(value: any) {
      const node = super.create(value) as HTMLElement;
      node.classList.add("ql_underline");
      return node;
    }
  }
  Quill.register(ReveUnderline, true);

  const Strike = Quill.import("formats/strike") as any;
  class ReveStrike extends Strike {
    static create(value: any) {
      const node = super.create(value) as HTMLElement;
      node.classList.add("ql_strike");
      return node;
    }
  }
  Quill.register(ReveStrike, true);

  // Subscript / superscript share the 'script' format
  const Script = Quill.import("formats/script") as any;
  class ReveScript extends Script {
    static create(value: any) {
      const node = super.create(value) as HTMLElement;
      node.classList.add(value === "sub" ? "ql_subscript" : "ql_superscript");
      return node;
    }
  }
  Quill.register(ReveScript, true);

  // ── Block blots ───────────────────────────────────────────────────────────

  const Blockquote = Quill.import("formats/blockquote") as any;
  class ReveBlockquote extends Blockquote {
    static create(value: any) {
      const node = super.create(value) as HTMLElement;
      node.classList.add("ql_blockquote");
      return node;
    }
  }
  Quill.register(ReveBlockquote, true);

  // Heading levels 1–6: add ql_heading{n} class alongside the <h*> tag
  const Header = Quill.import("formats/header") as any;
  class ReveHeader extends Header {
    static create(value: any) {
      const node = super.create(value) as HTMLElement;
      for (let i = 1; i <= 6; i++) node.classList.remove(`ql_heading${i}`);
      if (value && value !== false) {
        node.classList.add(`ql_heading${value}`);
      }
      return node;
    }
  }
  Quill.register(ReveHeader, true);

  // Quill ships no <hr> format, so an <hr> pasted or loaded into an editor was
  // dropped on the way in — a letterhead rule survived export but not a round
  // trip through the editor. That gap is what pushed the signature dialog into
  // storing its HTML as escaped source inside a <pre> (REV-2259): a code block
  // was the only blot that would hold markup Quill could not model. With a real
  // blot the rule survives, and processHtmlContent already knows how to turn an
  // <hr> into the ruled line both exports draw.
  const BlockEmbed = Quill.import("blots/block/embed") as any;
  class ReveHr extends BlockEmbed {
    static blotName = "hr";
    static tagName = "hr";
  }
  Quill.register(ReveHr, true);

  // ── Block attributors ─────────────────────────────────────────────────────

  // Align (overrides ql-align-* → ql_align-*)
  const ReveAlignClass = new ClassAttributor("align", "ql_align", {
    scope: Scope.BLOCK,
    whitelist: ["right", "center", "justify"],
  });
  Quill.register("attributors/class/align", ReveAlignClass, true);
  Quill.register("formats/align", ReveAlignClass, true);

  // Indent (overrides ql-indent-* → ql_indent-*, preserves +1/-1 increment logic)
  class ReveIndentAttributor extends ClassAttributor {
    add(node: HTMLElement, value: string | number): boolean {
      if (value === "+1" || value === "-1") {
        const current = parseInt(String(this.value(node) ?? "0"), 10);
        const next = current + (value === "+1" ? 1 : -1);
        if (next <= 0) {
          this.remove(node);
          return true;
        }
        return super.add(node, String(Math.min(next, 8)));
      }
      const num = Math.min(Math.max(parseInt(String(value), 10), 1), 8);
      return super.add(node, String(num));
    }
    canAdd(node: HTMLElement, value: string | number): boolean {
      return super.canAdd(node, String(value));
    }
    value(node: HTMLElement): number | undefined {
      return parseInt(String(super.value(node)), 10) || undefined;
    }
  }
  const reveIndentClass = new ReveIndentAttributor("indent", "ql_indent", {
    scope: Scope.BLOCK,
    whitelist: ["1", "2", "3", "4", "5", "6", "7", "8"],
  });
  Quill.register("attributors/class/indent", reveIndentClass, true);
  Quill.register("formats/indent", reveIndentClass, true);

  // Marks the lines llm-server appends as the profile signature, so regenerating
  // a letter can swap in the current one even after the user edited it here.
  const reveSignatureClass = new ClassAttributor("signature", "ql_signature", {
    scope: Scope.BLOCK,
    whitelist: ["line"],
  });
  Quill.register("attributors/class/signature", reveSignatureClass, true);
  Quill.register("formats/signature", reveSignatureClass, true);
}

/**
 * Enter at the end of a signature line starts an unmarked line. Quill carries block
 * formats onto the new line, which would make anything typed after the signature
 * part of it -- and dropped when the letter is regenerated.
 */
export const reveKeyboardBindings = {
  "signature exit": {
    key: "Enter",
    collapsed: true,
    format: ["signature"],
    suffix: /^$/,
    handler(this: { quill: Quill }, range: { index: number }, context: { format: Record<string, unknown> }) {
      this.quill.insertText(range.index, "\n", context.format, "user");
      this.quill.formatLine(range.index + 1, 1, "signature", false, "user");
      this.quill.setSelection(range.index + 1, 0, "silent");
      return false;
    },
  },
};
